import React, { useCallback, useContext, useEffect, useState } from 'react';
import i18next from 'i18next';
import _ from 'lodash';
import { Grid, Paper, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import RuleActivationErrors from '../../../../components/rules/RuleActivationErrors';
import EAButton from '../../../../components/common/EAButton';
import EARuleAccordion from '../../../../components/rules/EARuleAccordion';
import useStyles from '../../../../style/js-style/containers/administration/rules/RuleFormsStyle';
import { find } from 'lodash';
import EAIcon from '../../../../components/common/EAIcon';
import {
  RuleActionParameterNames,
  RuleActionType,
  RulesContext,
  RuleType,
  getAvailableOperatorOptions,
  parseTargetFieldFromCriterion,
  validateRule,
  TargetFieldCategory,
  getTargetFieldOptionsForThirdParty,
  isActionsWithWarning,
  parseTargetFieldCategoryFromCriterion,
  getTargetFieldOptionsForDocument,
  parseDocumentTypeIdFromCriterion,
  getFlatSubFieldsOptions,
  getTargetFieldOptionsForDossier,
  parseDossierTypeIdFromCriterion,
  RuleActionParameterOptions,
} from 'services/common/RulesUtils';
import clsx from 'clsx';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import moment from 'moment';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Context } from 'states/Store';
import EAColorIcon from '../../../../components/common/EAColorIcon';
import i18n from '../../../../i18n/i18n';
import { 
  isMatchingDossierTarget, 
  isMatchingThirdPartyTarget 
} from 'services/common/CalculatorUtils';

const RuleSummary = ({
  open = false,
  disabled = false,
  isEditionMode = false,
  isActiveRule = false,
  evaluatedRuleTargetResource = 0,
  onCancel = () => { },
  onContinue = () => { },
  onDelete = () => { },
  onDeactivate = () => { },
  readOnly = false
}) => {
  const [actionsListEmpty, setActionsListEmpty] = useState(false);
  const [noDossierSelected, setNoDossierSelected] = useState(false);
  const [hasEmptyRefs, setHasEmptyRefs] = useState(false);
  const [
    multipleSameActionNotAllowed,
    setMultipleSameActionNotAllowed,
  ] = useState(false);
  const [canRegisterAndEnable, setCanRegisterAndEnable] = useState(true);
  const [hasWarnings, setHasWarnings] = useState(false);

  const { classes } = useStyles();

  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const [state] = useContext(Context);
  const formMethods = useFormContext();
  const { control, getValues, setValue, handleSubmit } = formMethods;
  const ruleType = getValues('type');

  let criteria = useWatch({ control, name: `criteria` });
  let actions = useWatch({ control, name: `actions` });
  let name = useWatch({ control, name: 'name' });

  const handleContinue = async () => {
    onContinue();
  };

  const handleSave = () => {
    handleSubmit(onContinue)();
  };

  useEffect(() => {
    setHasWarnings(isActionsWithWarning(actions, state.dossierTypes));
  }, [actions, state.dossierTypes]);

  const handleSummaryEnable = (shouldEnable) => {
    const activationErrors = validateRule(actions);

    if (!activationErrors) {
      shouldEnable && setValue('enabled', true);
      handleSubmit(onContinue)();
    }

    if (activationErrors === 'no-actions') {
      setActionsListEmpty(true);
      setCanRegisterAndEnable(false);
    }
    if (activationErrors === 'multiple-actions-not-allowed') {
      setCanRegisterAndEnable(false);
      setMultipleSameActionNotAllowed(true);
    }
  };

  return (
    <EARuleAccordion
      buttons={[]}
      title={i18next.t('rules.summary.title')}
      icon="assignment_green"
      opened={open}
      disabled={disabled}
    >
      <div>
        <div className={classes.recapWrapper}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                <div className={classes.summaryTitle}>
                  <EAColorIcon
                    icon={'settings_applications_green'}
                    svgStyle={{ marginRight: '5px' }}
                  />
                  <span className={classes.sectionTitle}>
                    {i18next.t('rules.summary.ruleNameLabel') +
                      `${name ? ' : ' + name : ''}`}
                  </span>
                </div>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.summaryListContainer}>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={_.isEmpty(criteria) ? 0 : 5}
                    display="flex"
                  >
                    {!_.isEmpty(criteria) && (
                      <CriteriaSummary
                        criteria={criteria}
                        ruleType={ruleType}
                      />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={_.isEmpty(criteria) ? 8 : 5}
                    display="flex"
                  >
                    {!_.isEmpty(actions) && (
                      <ActionsSummary actions={actions} />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={_.isEmpty(criteria) ? 4 : 2}
                    display="flex"
                  >
                    {!_.isEmpty(actions) && (
                      <TargetSummary
                        evaluatedRuleTargetResource={
                          evaluatedRuleTargetResource
                        }
                        ruleType={ruleType}
                      />
                    )}
                  </Grid>

                  {hasWarnings && (
                    <Grid item xs={12}>
                      <RuleWarnings />
                    </Grid>
                  )}
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>

        {isEditionMode && (
          <RuleActivationErrors
            actionsListEmpty={actionsListEmpty}
            noDossierSelected={noDossierSelected}
            hasEmptyRefs={hasEmptyRefs}
            onActionsListEmptyClose={setActionsListEmpty}
            onNoDossierSelectedClose={setNoDossierSelected}
            onEmptyRefsClose={setHasEmptyRefs}
            multipleSameActionNotAllowed={multipleSameActionNotAllowed}
            onMultipleSameActionNotAllowedClose={
              setMultipleSameActionNotAllowed
            }
          />
        )}

        <div
          className={clsx(
            classes.recapButtonWrapper,
            isEditionMode && classes.recapButtonWrapperEdit
          )}
        >
          {isEditionMode ? (
            <>
              <div className={classes.summaryEditLeftBtns}>
                {/* Delete Rule */}
                {!readOnly &&
                  <EAButton
                    borderless={true}
                    colorVariant="error"
                    onClick={() => {
                      if (
                        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_RULE)
                      ) {
                        onDelete();
                      }
                    }}
                    disabled={
                      !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_RULE)
                    }
                    content={
                      <div
                        className={
                          !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_RULE)
                            ? classes.disabledDeleteBtn
                            : ''
                        }
                      >
                        <EAIcon
                          icon={'delete_red'}
                          className={classes.deleteRuleImg}
                        />
                        {i18next.t('rules.summary.actions.delete')}
                      </div>
                    }
                    customStyle={{ fontSize: '14px' }}
                  />
                }
              </div>
              <div className={classes.summaryEditRightBtns}>
                {/* Cancel */}
                <EAButton
                  onClick={(e) => {
                    e.preventDefault();
                    onCancel();
                    setCanRegisterAndEnable(true);
                    setActionsListEmpty(false);
                    setNoDossierSelected(false);
                    setHasEmptyRefs(false);
                    setMultipleSameActionNotAllowed(false);
                  }}
                  content={i18next.t('rules.summary.actions.cancel')}
                  colorVariant="secondary"
                  customStyle={{
                    fontSize: '14px',
                    minWidth: '80px',
                    width: '80px',
                    minHeight: '32px',
                    height: '32px',
                    padding: 0,
                    borderRadius: '12px',
                  }}
                />
                {/* Save Rule */}
                <EAButton
                  onClick={(e) => {
                    e.preventDefault();
                    isActiveRule ? handleSummaryEnable(false) : handleSave();
                  }}
                  content={i18next.t('rules.summary.actions.save')}
                  colorVariant={'primaryLight'}
                  disabled={(isActiveRule && !canRegisterAndEnable) || readOnly}
                  customStyle={{
                    fontSize: '14px',
                    width: '130px',
                    minHeight: '32px',
                    height: '32px',
                    padding: 0,
                    borderRadius: '12px',
                  }}
                />
                {/* Activate / Deactivate Rule */}
                {isActiveRule ? (
                  <EAButton
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        hasAnyOfPrivileges(
                          PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS
                        )
                      ) {
                        onDeactivate();
                      }
                    }}
                    disabled={
                      !hasAnyOfPrivileges(
                        PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS
                      ) || readOnly
                    }
                    content={i18next.t('rules.summary.actions.deactivate')}
                    colorVariant={'success'}
                    customStyle={{
                      fontSize: '14px',
                      width: '120px',
                      minHeight: '32px',
                      height: '32px',
                      padding: 0,
                      borderRadius: '12px',
                    }}
                  />
                ) : (
                  <EAButton
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        hasAnyOfPrivileges(
                          PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS
                        )
                      ) {
                        handleSummaryEnable(true);
                      }
                    }}
                    disabled={
                      !hasAnyOfPrivileges(
                        PrivilegeEnum.PRIVILEGE_EDIT_RULE_STATUS
                      ) || !canRegisterAndEnable || readOnly
                    }
                    content={i18next.t('rules.summary.actions.activate')}
                    customStyle={{
                      fontSize: '14px',
                      width: '120px',
                      minHeight: '32px',
                      height: '32px',
                      padding: 0,
                      borderRadius: '12px',
                    }}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              {/* Cancel and go back */}
              <EAButton
                onClick={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
                content={i18next.t('rules.summary.actions.cancel')}
                colorVariant="secondary"
                customStyle={{
                  fontSize: '14px',
                  width: '178px',
                  minHeight: '32px',
                  height: '32px',
                  padding: 0,
                  borderRadius: '12px',
                }}
              />

              {/* Validate and continue to finalize */}
              <EAButton
                onClick={(e) => {
                  e.preventDefault();
                  handleContinue();
                }}
                content={i18next.t('rules.summary.actions.validate')}
                customStyle={{
                  fontSize: '14px',
                  width: '178px',
                  minHeight: '32px',
                  height: '32px',
                  padding: 0,
                  borderRadius: '12px',
                }}
              />
            </>
          )}
        </div>
      </div>
    </EARuleAccordion>
  );
};

const CriteriaSummary = ({ criteria, ruleType }) => {
  const { classes } = useStyles();
  const [criteriaToDisplay, setCriteriaToDisplay] = useState([]);

  const {
    activities,
    countries,
    thirdpartyAttributes,
    dossierAttributes,
    calculatedAttributes,
    thirdPartyIndicators,
    sourceData,
    financialStrengthScores,
    financialStrengthRatings,
    showInduedScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    enableTransparencyScore,
    documentTypes,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    dossierTypes,
    lang,
  } = useContext(RulesContext);

  const translateField = useCallback((fieldName, availableTargetFields) => {
    let entry = find(availableTargetFields, { value: fieldName });
    return entry === undefined ? fieldName : entry.label;
  }, []);

  const translateOperator = useCallback(
    (operator, field, availableTargetFields) => {
      const criterionFieldEntry = _.find(availableTargetFields, {
        value: field,
      });
      let availableCriterionOperatorOptions = getAvailableOperatorOptions(
        criterionFieldEntry
      );
      let entry = find(availableCriterionOperatorOptions, { value: operator });
      return entry === undefined ? operator : entry.label.toUpperCase();
    },
    []
  );

  const translateValues = useCallback(
    (values, fieldName, availableTargetFields) => {
      let fieldEntry = find(availableTargetFields, { value: fieldName });
      let valueOptions =
        fieldEntry === undefined ? [] : fieldEntry.valueOptions;

      if (valueOptions && valueOptions.length > 0) {
        return values.map((value) => {
          let valueEntry = find(valueOptions, { value: value });
          return valueEntry === undefined ? value : valueEntry.label;
        });
      } else {
        if (
          fieldEntry?.type === 'ATTRIBUTE' &&
          fieldEntry?.subType === 'date'
        ) {
          return values.map((d) =>
            moment(d).format(i18next.t('date.dateFormat'))
          );
        }
        return values;
      }
    },
    []
  );

  const mapCriteria = useCallback(() => {
    let filteredCriteria = criteria.filter((c) => c.targetField !== '');

    return filteredCriteria.map((criterion) => {
      let availableTargetFields = [];
      const targetFieldCategory = parseTargetFieldCategoryFromCriterion(
        criterion
      );
      switch (targetFieldCategory) {
        case TargetFieldCategory.THIRD_PARTY:
          availableTargetFields = getFlatSubFieldsOptions(
            getTargetFieldOptionsForThirdParty({
              countries: countries,
              activities: activities,
              thirdpartyAttributes: thirdpartyAttributes,
              calculatedAttributes: _.filter(calculatedAttributes, item => isMatchingThirdPartyTarget(item)),
              indicators: _.filter(thirdPartyIndicators, item => isMatchingThirdPartyTarget(item)),
              sourceData: sourceData,
              financialStrengthScores: financialStrengthScores,
              financialStrengthRatings: financialStrengthRatings,
              withInduedScores: showInduedScores,
              withTransparencyScore: showTransparencyScore,
              withSourceData: showSourceData,
              withFinancialStrengthScores: showFinancialStrengthScores,
              withEsgScores: showEsgScores,
              enableTransparencyScore: enableTransparencyScore,
              withEcovadisScores: showEcovadisScores,
              withSecurityRatingScores: showSecurityRatingScores,
              withCreditsafeScores: showCreditsafeScores,
              lang: lang,
            })
          );
          break;

        case TargetFieldCategory.DOSSIER:
          availableTargetFields = getTargetFieldOptionsForDossier(
            dossierAttributes,
            _.filter(calculatedAttributes, item => isMatchingDossierTarget(item, parseDossierTypeIdFromCriterion(criterion))),
            _.filter(thirdPartyIndicators, item => isMatchingDossierTarget(item, parseDossierTypeIdFromCriterion(criterion)))
          );
          break;

        case TargetFieldCategory.DOCUMENT:
          availableTargetFields = getFlatSubFieldsOptions(
            getTargetFieldOptionsForDocument(
              _.find(documentTypes, {
                value: parseDocumentTypeIdFromCriterion(criterion),
              })?.properties || []
            )
          );
          break;

        default:
          break;
      }
      const field = parseTargetFieldFromCriterion(criterion);
      const documentType = _.find(documentTypes, {
        value: parseDocumentTypeIdFromCriterion(criterion),
      });
      const dossierType = _.find(dossierTypes, {
        value: parseDossierTypeIdFromCriterion(criterion),
      });
      return {
        dossierTypeLabel: dossierType?.label,
        documentTypeLabel: documentType?.label,
        criteriaField: translateField(field, availableTargetFields),
        criteriaOperator: translateOperator(
          criterion.operator,
          field,
          availableTargetFields
        ),
        criteriaValues: translateValues(
          criterion.values,
          field,
          availableTargetFields
        ),
      };
    });
  }, [
    countries,
    activities,
    thirdpartyAttributes,
    calculatedAttributes,
    thirdPartyIndicators,
    sourceData,
    financialStrengthScores,
    financialStrengthRatings,
    showInduedScores,
    showEcovadisScores,
    showSecurityRatingScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    showCreditsafeScores,
    enableTransparencyScore,
    documentTypes,
    dossierTypes,
    dossierAttributes,
    criteria,
    translateField,
    translateOperator,
    translateValues,
    lang,
  ]);

  useEffect(() => {
    setCriteriaToDisplay(mapCriteria());
  }, [mapCriteria]);

  const getRuleTypeDisplay = (type) => {
    switch (type) {
      case RuleType.ON_THIRD_PARTY:
        return {
          icon: 'thirdparty_green',
          text: i18next.t('rules.summary.thirdparty')
        };
      case RuleType.ON_DOSSIER:
        return {
          icon: 'folder_open_small',
          text: i18next.t('rules.summary.dossier')
        };
      case RuleType.ON_CONTEXT:
        return {
          icon: 'context/contextModel/context',
          text: i18next.t('contexte')
        };
      case RuleType.ON_CONTEXT_THIRD_PARTY:
        return {
          icon: 'thirdparty_green',
          text: i18n.t('contextAuTiers')
        };
      case RuleType.ON_DOCUMENT:
        return {
          icon: 'document_small',
          text: i18next.t('rules.summary.document') || i18next.t('documents')
        };
      default:
        return;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        position: 'relative',
        marginTop: '16px',
        backgroundColor: '#FAFBFD',
        flexGrow: 1,
      }}
    >
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesConditionLabel')}
      </div>
      <Typography variant="p">
        <div className={classes.summaryListTitle}>
          <span>{i18next.t('rules.summary.ruleCriteriaAppliedToLabel')}</span>

          {/*TODO à revoir après l'ajout des conditions sur dossier et document */}
          <div className={classes.summaryListCategory}>
            {(() => {
              const ruleTypeDisplay = getRuleTypeDisplay(ruleType);
              if (!ruleTypeDisplay) return null;
              const { icon, text } = ruleTypeDisplay;
              return (
                <span>
                  <EAColorIcon
                    icon={icon}
                    svgStyle={{ margin: '0 4px' }}
                  />
                  <b>{text}</b>
                </span>
              );
            })()}
          </div>

          <span>{i18next.t('rules.summary.ruleCriteriaWhenLabel')}</span>
        </div>
      </Typography>

      <Typography component="div" className={classes.blocValues}>
        {criteriaToDisplay?.map((val, index) => {
          return (
            <div
              key={`${val.criteriaField}-${index}`}
              className={classes.summaryListItem}
            >
              {/* TODO : to be added later
            <span className={classes.summaryListIndex}>
              {`${index + 1}`}
            </span> */}
              <EAIcon
                icon="arrow/arrow-to-right_large_grey"
                className={classes.summaryListArrow}
              />

              {val.documentTypeLabel && (
                <span className={classes.summaryListField}>
                  {val.documentTypeLabel}
                </span>
              )}

              {val.dossierTypeLabel && (
                <span className={classes.summaryListField}>
                  {val.dossierTypeLabel}
                </span>
              )}

              <span className={classes.summaryListField}>
                {val.criteriaField}
              </span>

              <span className={classes.summaryListOperator}>
                {val.criteriaOperator}
              </span>

              {val.criteriaValues?.map((value, index) => {
                return (
                  <span key={index} className={classes.summaryListValue}>
                    {value}
                  </span>
                );
              })}
            </div>
          );
        })}
      </Typography>
    </Paper>
  );
};

const ActionsSummary = ({ actions }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        position: 'relative',
        marginTop: '16px',
        backgroundColor: '#FAFBFD',
        flexGrow: 1,
      }}
    >
      <DossierCreationActionsSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.DOSSIER_CREATION,
            RuleActionType.ADD_DOSSIER_CONTEXT_THIRDPARTY,
          ].includes(entry.type)
        )}
      />

      <DossierStopActionSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.DOSSIER_STOP,
            RuleActionType.ARCHIVE_DOSSIER_CONTEXT_THIRDPARTY,
          ].includes(entry.type)
        )}
      />

      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.FINANCIAL_STRENGTH_ACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleActiverFinancialStrengthActionsSummaryTitle'
        )}
      />
      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) =>
            entry.type === RuleActionType.FINANCIAL_STRENGTH_DEACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleDesactiverFinancialStrengthActionsSummaryTitle'
        )}
      />
      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.DNB_ESG_ACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleActiverEsgActionsSummaryTitle'
        )}
      />
      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.DNB_ESG_DEACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleDesactiverEsgActionsSummaryTitle'
        )}
      />
      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.CREDITSAFE_SCORE_ACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleActivateCreditsafeActionsSummaryTitle'
        )}
      />
      <ScoreActionsSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.CREDITSAFE_SCORE_DEACTIVATION
        )}
        actionTypeLabel={i18next.t(
          'rules.summary.ruleDectivateCreditsafeActionsSummaryTitle'
        )}
      />

      <DocumentActionsSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.DOCUMENT_ADD,
            RuleActionType.DOCUMENT_REMOVE,
          ].includes(entry.type)
        )}
      />

      <ThirdPartyStopActionSummary
        actions={actions?.filter(
          (entry) => entry.type === RuleActionType.THIRD_PARTY_STOP
        )}
      />

      <ThirdPartyAddArchiveToContextActionSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.ADD_THIRD_PARTY_TO_CONTEXT,
            RuleActionType.ARCHIVE_THIRD_PARTY_FROM_CONTEXT,
          ].includes(entry.type)
        )}
      />

      <ContextActivateArchiveContextTpActionSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.ACTIVATE_CONTEXT_THIRDPARTY,
            RuleActionType.ARCHIVE_CONTEXT_THIRDPARTY,
          ].includes(entry.type)
        )}
      />

      <DossierTypeChangeActionsSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.DOSSIER_TYPE_CHANGE
          ].includes(entry.type)
        )}
      />

      <DocumentStatusChangeActionSummary
        actions={actions?.filter((entry) =>
          [
            RuleActionType.DOCUMENT_STATUS_CHANGE
          ].includes(entry.type)
        )}
      />
    </Paper>
  );
};

const TargetSummary = ({ evaluatedRuleTargetResource = 0, ruleType = '' }) => {
  const { classes } = useStyles();

  const getTargetDisplay = (ruleType) => {
    switch (ruleType) {
      case RuleType.ON_THIRD_PARTY:
        return {
          icon: 'thirdparty_green',
          text: i18next.t('thirdParties')
        };
      case RuleType.ON_DOSSIER:
        return {
          icon: 'folder_open_small',
          text: i18next.t('dossiers')
        };
      case RuleType.ON_CONTEXT:
        return {
          icon: 'context/contextModel/context',
          text: i18next.t('contexte')
        };
      case RuleType.ON_CONTEXT_THIRD_PARTY:
        return {
          icon: 'thirdparty_green',
          text: i18n.t('contextAuTiers')
        };
      case RuleType.ON_DOCUMENT:
        return {
          icon: 'document_small',
          text: i18next.t('documents')
        };
      default:
        return;
    }
  };
  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        position: 'relative',
        marginTop: '16px',
        backgroundColor: '#FAFBFD',
        flexGrow: 1,
      }}
    >
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesTargetLabel')}
      </div>
      <div className={classes.typeWrapper}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {(() => {
            const ruleTypeDisplay = getTargetDisplay(ruleType);
            if (!ruleTypeDisplay) return null;
            const { icon, text } = ruleTypeDisplay;
            return (
              <>
                <EAColorIcon icon={icon} height="20px" width={'20px'} />
                <span className={classes.summaryTarget}>
                  {text} : {evaluatedRuleTargetResource}
                </span>
              </>
            );
          })()}
        </div>
      </div>
    </Paper>
  );
};

const ScoreActionsSummary = ({ actions, actionTypeLabel }) => {
  const { classes } = useStyles();

  if (!actions || actions.length === 0) {
    return <></>;
  }
  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>
      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>{actionTypeLabel}</div>
            </Typography>

            {/* Reverse Action */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 48 }}
                  className={classes.summaryActionArrow}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginInlineEnd: 4,
                  }}
                >
                  {action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] &&
                    action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] === 'true' ? (
                    <CheckBox style={{ color: '#2d9f40' }} />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </div>
                <span style={{ flex: 1 }}>
                  {i18next.t(
                    `rules.configuration.ruleActions.fields.parameters.reverseAction.label`
                  )}
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const RuleWarnings = () => {
  const { classes } = useStyles();

  return (
    <Paper
      display="flex"
      alignItems="center"
      elevation={0}
      sx={{
        padding: 2,
        position: 'relative',
        marginTop: '16px',
        backgroundColor: '#FAFBFD',
      }}
    >
      <div className={classes.summaryBlocLabel}>
        <EAIcon icon="warning" style={{ marginRight: '4px' }} />
        {i18next.t('rules.summary.warnings.title')}
      </div>
      <Typography variant="body2">
        <div className={classes.summaryListItem}>
          {i18next.t('rules.summary.warnings.consumption')}
        </div>
      </Typography>
    </Paper>
  );
};

const DossierCreationActionsSummary = ({ actions }) => {
  const { classes } = useStyles();

  const { dossierTypes } = useContext(RulesContext);

  const translateDossierType = useCallback(
    (code) => {
      let entry = find(dossierTypes, { value: code });
      return entry === undefined ? code : entry.label;
    },
    [dossierTypes]
  );

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      {/* Only for dossier creation : */}
      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {i18next.t(
                  `rules.summary.ruleCreationDossierActionsSummaryTitle`
                )}
              </div>
            </Typography>

            {/* Dossier type */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.dossierType`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                    {`${translateDossierType(
                      action.parameters[
                      RuleActionParameterNames.DOSSIER_TYPE_CODE
                      ]
                    )} `}
                  </span>
                </span>
              </div>
            </Typography>

            {/* Dossier reference */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 24 }}
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.withReference`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                    {
                      action.parameters[
                      RuleActionParameterNames.DOSSIER_REFERENCE
                      ]
                    }
                  </span>
                </span>
              </div>
            </Typography>

            {/* Reactivate Dossier */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 48 }}
                  className={classes.summaryActionArrow}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginInlineEnd: 4,
                  }}
                >
                  {action.parameters[
                    RuleActionParameterNames.DOSSIER_REACTIVATE_ARCHIVED
                  ] &&
                    action.parameters[
                    RuleActionParameterNames.DOSSIER_REACTIVATE_ARCHIVED
                    ] !== 'false' ? (
                    <CheckBox style={{ color: '#2d9f40' }} />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </div>
                <span>
                  {i18next.t(
                    `rules.configuration.ruleActions.fields.parameters.reactivateDossier.label`
                  )}
                </span>
              </div>
            </Typography>

            {/* Reverse Action */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 48 }}
                  className={classes.summaryActionArrow}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginInlineEnd: 4,
                  }}
                >
                  {action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] &&
                    action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] === 'true' ? (
                    <CheckBox style={{ color: '#2d9f40' }} />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </div>
                <span style={{ flex: 1 }}>
                  {i18next.t(
                    `rules.configuration.ruleActions.fields.parameters.reverseAction.label`
                  )}
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const DocumentActionsSummary = ({ actions }) => {
  const { classes } = useStyles();

  const { documentTypes } = useContext(RulesContext);

  const translateDocumentType = useCallback(
    (value) => {
      let entry = find(documentTypes, { value: value });
      return entry ? entry.label : value;
    },
    [documentTypes]
  );

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {action.type === RuleActionType.DOCUMENT_ADD
                  ? i18next.t(
                    `rules.summary.ruleDocumentAddActionsSummaryTitle`
                  )
                  : i18next.t(
                    `rules.summary.ruleDocumentRemoveActionsSummaryTitle`
                  )}
              </div>
            </Typography>

            {/* Document type */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.selectedDocument`)}</span>
                {action.parameters[
                  RuleActionParameterNames.DOCUMENT_TYPE_IDS
                ] &&
                  action.parameters[RuleActionParameterNames.DOCUMENT_TYPE_IDS]
                    .split(',')
                    .map((value, valueIndex) => (
                      <span key={`${action.id}-${index}-${valueIndex}`} className={classes.summaryRefField}>
                        <span>{`${translateDocumentType(value)} `}</span>
                      </span>
                    ))}
              </div>
            </Typography>
            {/* Reverse Action */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 48 }}
                  className={classes.summaryActionArrow}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginInlineEnd: 4,
                  }}
                >
                  {action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] &&
                    action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] === 'true' ? (
                    <CheckBox style={{ color: '#2d9f40' }} />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </div>
                <span style={{ flex: 1 }}>
                  {i18next.t(
                    `rules.configuration.ruleActions.fields.parameters.reverseAction.label`
                  )}
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const DossierStopActionSummary = ({ actions }) => {
  const { classes } = useStyles();

  const { dossierTypes } = useContext(RulesContext);

  const translateDossierType = useCallback(
    (code) => {
      let entry = find(dossierTypes, { value: code });
      return entry === undefined ? code : entry.label;
    },
    [dossierTypes]
  );

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {i18next.t(`rules.summary.ruleStopDossierActionsSummaryTitle`)}
              </div>
            </Typography>

            {/* Dossier type */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.dossierType`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                    {`${translateDossierType(
                      action.parameters[
                      RuleActionParameterNames.DOSSIER_TYPE_CODE
                      ]
                    )} `}
                  </span>
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const ThirdPartyStopActionSummary = ({ actions }) => {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {i18next.t(
                  `rules.summary.ruleStopThirdPartyActionsSummaryTitle`
                )}
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const ThirdPartyAddArchiveToContextActionSummary = ({ actions }) => {
  const { classes } = useStyles();
  const { contextModels } = useContext(RulesContext);
  const ruleTitles = {
    [RuleActionType.ADD_THIRD_PARTY_TO_CONTEXT]: i18next.t(`rules.summary.ruleAddThirdpartyToContext`),
    [RuleActionType.ARCHIVE_THIRD_PARTY_FROM_CONTEXT]: i18next.t(`rules.summary.ruleRemoveThirdpartyFromContext`)
  }

  const getContextName = (action) => {
    const found = contextModels.find(
      (model) => model.id === Number(action.parameters.context_model_id)
    );
    return found?.name ?? '';
  };

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>
      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {ruleTitles[action.type]}
              </div>
            </Typography>

            {/* Context Model name */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.contextModel`)}</span>
                <span className={classes.summaryRefField}>
                  <span>{getContextName(action)}</span>
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const ContextActivateArchiveContextTpActionSummary = ({ actions }) => {
  const { classes } = useStyles();

  const ruleTitles = {
    [RuleActionType.ARCHIVE_CONTEXT_THIRDPARTY]: i18next.t(`rules.summary.ruleArchiveContextThirdparty`),
    [RuleActionType.ACTIVATE_CONTEXT_THIRDPARTY]: i18next.t(`rules.summary.ruleActivateContextThirdparty`)
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>
      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {ruleTitles[action.type]}
              </div>
            </Typography>
            {RuleActionType.ACTIVATE_CONTEXT_THIRDPARTY === action.type &&
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 48 }}
                  className={classes.summaryActionArrow}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginInlineEnd: 4,
                  }}
                >
                  {action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] &&
                    action.parameters[RuleActionParameterNames.REVERSE_ACTION_EXECUTION_ACTIVATED] === 'true' ? (
                    <CheckBox style={{ color: '#2d9f40' }} />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )}
                </div>
                <span style={{ flex: 1 }}>
                  {i18next.t(
                    `rules.configuration.ruleActions.fields.parameters.reverseAction.label`
                  )}
                </span>
              </div>
            </Typography>
            }
          </div>
        ))}
      </Typography>
    </>
  );
};

const DossierTypeChangeActionsSummary = ({ actions }) => {
  const { classes } = useStyles();

  const { dossierTypes } = useContext(RulesContext);

  const translateDossierType = useCallback(
    (code) => {
      let entry = find(dossierTypes, { code: code });
      return entry === undefined ? code : entry.label;
    },
    [dossierTypes]
  );

  const translateOptions = useCallback(
    (code) => {
      let entry = find(RuleActionParameterOptions, { value: code });
      return entry === undefined ? undefined : i18next.t(`rules.configuration.ruleActions.fields.parameters.dossierTypeChangeOptions.options.${entry.label}`);
    },
    []
  );

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      {/* Only for dossier creation : */}
      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {i18next.t(
                  `rules.summary.ruleDossierTypeChangeActionsSummaryTitle`
                )}
              </div>
            </Typography>

            {/* Dossier type */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.dossierType`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                    {`${translateDossierType(
                      action.parameters[
                        RuleActionParameterNames.DOSSIER_TYPE_CODE
                      ]
                    )} `}
                  </span>
                </span>
              </div>
            </Typography>

            {/* Option */}
            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  style={{ marginInlineStart: 24 }}
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.changeDossierTypeOption`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                     {`${translateOptions(
                      action.parameters[
                        RuleActionParameterNames.DOSSIER_TYPE_CHANGE_OPTION
                      ]
                    )} `}
                  </span>
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
};

const DocumentStatusChangeActionSummary = ({ actions }) => {
  const { classes } = useStyles();

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={classes.summaryBlocLabel}>
        {i18next.t('rules.summary.rulesActionLabel')}
      </div>

      <Typography component="div" className={classes.blocValues}>
        {actions?.map((action, index) => (
          <div
            key={`${action.id}-${index}`}
            className={classes.summaryListItem}
          >
            {/* Action type */}
            <Typography variant="p">
              <div className={classes.summaryListTitle}>
                {i18next.t(
                  `rules.summary.ruleDocumentStatusChangeActionSummaryTitle`
                )}
              </div>
            </Typography>

            <Typography variant="p">
              <div className={classes.summaryActionLabel}>
                <EAIcon
                  icon="arrow/arrow-angle_grey"
                  className={classes.summaryActionArrow}
                />
                <span>{i18next.t(`rules.summary.newStatus`)}</span>
                <span className={classes.summaryRefField}>
                  <span>
                    {
                      i18next.t(
                        `viewer.status.statusLabel.${action.parameters[
                          RuleActionParameterNames.DOCUMENT_STATUS_CODE
                        ]}`
                      )
                    }
                  </span>
                </span>
              </div>
            </Typography>
          </div>
        ))}
      </Typography>
    </>
  );
}

export default RuleSummary;
